import { cwd } from 'node:process';
import { Command } from '@commander-js/extra-typings';
import { getUserInfo } from '@wix/cli-auth';
import { getErrorLogFilePath, SENTRY_DSN } from '@wix/create-app-definitions';
import { createErrorHandler } from '@wix/cli-error-reporting';
import { getAppCommand } from '@wix/app-command';
import {
  getHeadlessCommand,
  getHeadlessInitCommand,
} from '@wix/headless-command';
import { createPanorama } from '@wix/cli-panorama';
import { setupTestEnv } from '@wix/cli-test-overrides';
import { getRepoType, createPackageManager } from '@wix/package-manager';
import { determineAgent } from '@vercel/detect-agent';
import { randomUUID } from 'node:crypto';
import pkgJson from '../package.json' with { type: 'json' };

setupTestEnv();

const targetParentFolder = cwd();

const {
  handler: errorHandler,
  errorReporter,
  registerToProcessErrors,
} = createErrorHandler({
  dsn: SENTRY_DSN,
  release: pkgJson.version,
  debugOutputPath: getErrorLogFilePath(targetParentFolder),
});

registerToProcessErrors();

const userInfo = await getUserInfo();
if (userInfo) {
  errorReporter.setUser({ id: userInfo.userId });
}

const cliSessionId = randomUUID();
errorReporter.setCliSessionId(cliSessionId);

const defaultAgentDetectionResult = {
  agent: undefined,
  isAgent: false,
} as const;

const aiAgent = await determineAgent().catch((error: unknown) => {
  errorReporter.reportError(error);
  return defaultAgentDetectionResult;
});

const aiAgentName = aiAgent.agent?.name;
const loginMode = aiAgent.isAgent ? 'agent' : 'interactive';
if (aiAgentName != null) {
  errorReporter.setAiAgentName(aiAgentName);
}

const panorama = await createPanorama({
  artifact: pkgJson.wix.artifact,
  version: pkgJson.version,
  userId: userInfo?.userId,
  errorReporter,
  cliSessionId,
  aiAgentName,
});

const repoType = await getRepoType(targetParentFolder);
const packageManager = await createPackageManager(repoType);

// ToDo: Uncomment this when first version of the package is released.
// await validateForLatestVersion(pkgJson, packageManager);

const program = new Command()
  .name(pkgJson.name)
  .version(pkgJson.version, '-v, --version');

program.addCommand(
  getAppCommand({
    errorReporter,
    userInfo,
    panorama,
    cliVersion: pkgJson.version,
    cliSessionId,
    loginMode,
    targetParentFolder,
    packageManager,
    repoType,
    aiAgentName,
  })
);

program.addCommand(
  getHeadlessCommand({
    errorReporter,
    userInfo,
    panorama,
    cliVersion: pkgJson.version,
    cliSessionId,
    loginMode,
    targetParentFolder,
    aiAgentName,
  })
);

program.addCommand(
  getHeadlessInitCommand({
    errorReporter,
    userInfo,
    panorama,
    cliVersion: pkgJson.version,
    cliSessionId,
    loginMode,
    targetParentFolder,
    aiAgentName,
  })
);

try {
  await program.parseAsync();
} catch (error) {
  errorHandler({ error });
}
